<template>
  <SettingsRow v-if="phase === 'error'" name="Call field mapping">
    <p class="stateLine error">Couldn’t load the mappings — try again shortly.</p>
  </SettingsRow>

  <template v-else-if="phase === 'ready'">
    <SettingsRow
      name="Record attribution"
      :description="`Who the call records created in ${providerLabel} are credited to.`"
    >
      <dl class="attr">
        <template v-if="createdByName">
          <dt>Created by</dt>
          <dd class="who">{{ createdByName }}</dd>
        </template>
        <dt>Owned by</dt>
        <dd>The {{ providerLabel }} user chosen in Preferences by the rep who made the call.</dd>
      </dl>
    </SettingsRow>

    <SettingsRow name="Call field mapping" description="The call fields we write to your CRM.">
      <p v-if="!rows.length" class="stateLine">
        {{ provider === 'hubspot'
          ? 'Calls are logged with HubSpot’s standard call fields.'
          : 'No call mappings configured yet.' }}
      </p>
      <div v-else class="map">
        <div v-for="row in rows" :key="row.sourceField + '→' + row.crmField" class="rowWrap">
          <div class="mapRow">
            <span class="mapSrc">{{ row.sourceLabel }}</span>
            <span class="mapArrow">→</span>
            <span class="mapDst">{{ row.crmFieldLabel }}</span>
          </div>
          <!-- Per-value mapping for enum sources (call type, disposition, …) —
               the nesting says "these are this row's values"; no caption needed. -->
          <div v-if="row.valuePairs" class="subMap">
            <div v-for="pair in row.valuePairs" :key="pair.code" class="mapRow">
              <span class="mapSrc">{{ pair.from }}</span>
              <span class="mapArrow">→</span>
              <span class="mapDst" :class="{ muted: !pair.to }">{{ pair.to || 'not set' }}</span>
            </div>
          </div>
        </div>
      </div>
      <p v-if="usingDefaults" class="note">Using the default mapping.</p>
      <p v-if="isAdmin" class="note">You can edit these anytime from the integration settings page.</p>
    </SettingsRow>
  </template>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import {
  crmUserLabel,
  getCallMappingSnapshot,
  type CrmExportProvider,
  type MappingRowView,
} from '../../../../services/crmExportApi'
import { useCrmExport } from '../../../composables/useCrmExport'
import { useTelephony } from '../../../composables/useTelephony'
import SettingsRow from './SettingsRow.vue'

// Read-only view of the call→CRM activity mapping.

type Phase = 'loading' | 'off' | 'no_connection' | 'error' | 'ready'

const { isAdmin } = useTelephony()
const crmExport = useCrmExport()

const phase = ref<Phase>('loading')
const provider = ref<CrmExportProvider | null>(null)
const createdByName = ref('')
const usingDefaults = ref(false)
const rows = ref<MappingRowView[]>([])

const providerLabel = computed(
  () => ({ salesforce: 'Salesforce', hubspot: 'HubSpot' })[provider.value || ''] || '',
)
onMounted(load)

watch(() => crmExport.enabled.value, () => { void load() })

async function load() {
  phase.value = 'loading'
  try {
    await crmExport.refresh()
    if (!crmExport.loaded.value) {
      phase.value = 'error'
      return
    }
    const exp = crmExport.settings.value!
    if (!exp.crm_export_enabled) {
      phase.value = 'off'
      return
    }
    if (!exp.provider) {
      phase.value = 'no_connection'
      return
    }
    provider.value = exp.provider
    createdByName.value = crmUserLabel(exp.connection_user)
    const snapshot = await getCallMappingSnapshot(exp.provider)
    usingDefaults.value = snapshot.usingDefaults
    rows.value = snapshot.rows
    phase.value = 'ready'
  } catch (e) {
    console.warn('[CrmExportSurface] load failed', e)
    phase.value = 'error'
  }
}
</script>

<style scoped>
.stateLine { margin: 0; font-size: var(--fs-sm); line-height: var(--lh-normal); color: var(--color-text-tertiary); }
.stateLine.error { color: var(--color-danger); }
.note { margin: 0; font-size: var(--fs-xs); color: var(--color-text-tertiary); }

.attr { margin: 0; display: grid; grid-template-columns: auto 1fr; gap: var(--space-1) var(--space-4); font-size: var(--fs-sm); }
.attr dt { color: var(--color-text-tertiary); }
.attr dd { margin: 0; color: var(--color-text-primary); }
.attr dd.who { font-weight: var(--fw-medium); }

.map {
  width: 100%; max-width: 520px; display: flex; flex-direction: column;
  border: 1px solid var(--color-border-subtle); border-radius: var(--radius-md);
}
.rowWrap { display: flex; flex-direction: column; gap: var(--space-1); padding: 8px 14px; }
.rowWrap + .rowWrap { border-top: 1px solid var(--color-border-subtle); }
.mapRow { display: grid; grid-template-columns: 1fr auto 1fr; align-items: baseline; gap: var(--space-2); }
.mapSrc { font-size: var(--fs-xs); color: var(--color-text-secondary); }
.mapArrow { font-size: var(--fs-xs); color: var(--color-text-tertiary); }
.mapDst { font-size: var(--fs-xs); color: var(--color-text-primary); overflow-wrap: anywhere; }
.mapDst.muted { color: var(--color-text-tertiary); }
/* Nested value mapping — ExportTab's .exp-submap look: inset with a left rule. */
.subMap {
  margin-left: var(--space-2);
  padding: var(--space-1) 0 var(--space-1) var(--space-3);
  border-left: 2px solid var(--color-border-subtle);
  display: flex; flex-direction: column; gap: var(--space-1);
}
</style>
